/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import { errorToString } from '@msteams/services-utilities-error';
import { userSettingsVar } from '../../apollo/reactiveVariables/UserSettingsVariables';
import { LogCategory } from '../../logger';
import { GetUserSettingsClient, UpdateUserSettingsClient, UserSettings, RequestPageVersion } from '../../service/gen';
import { getAppHost } from '../../utilities/TeamsUtils';

const getUserSettingsRepr = (settings: UserSettings | null): string =>
    settings ? `[UserSettings keys=[${Object.keys(settings).join(', ')}]]` : '[UserSettings null]';

export const initializeUserSettingsAsync = async (): Promise<void> => {
    const host = getAppHost();
    const logger = getAppHost().getLogger();

    let settings: UserSettings;
    if (host.getEcsConfig().MicrosoftTeamApprovals.ShowNewRequestPageToggleButton) {
        const startTime = performance.now();
        try {
            settings = await new GetUserSettingsClient().getUserSettings();
            logger.logTraceInfo(
                LogCategory.UserSettings,
                `Succeded to retrieve ${getUserSettingsRepr(settings)}, elapsed ${performance.now() - startTime}`
            );
        } catch (e) {
            logger.logTraceError(
                LogCategory.UserSettings,
                `Failed to initialize UserSettings with error ${errorToString(e)}, elapsed ${
                    performance.now() - startTime
                }`
            );
        }
    }
    settings = settings ?? {};
    migrateUserSettings(settings);
    userSettingsVar(settings);
};

// This function modifies settings in place, to assign fallback values to the fields of UserSettings and also allow
// to migrate outdated values.
const migrateUserSettings = (settings: UserSettings): void => {
    const { ShowNewRequestPageToggleButton, RequestPageVersionByDefault } =
        getAppHost().getEcsConfig().MicrosoftTeamApprovals;

    settings.RequestPageVersion =
        settings.RequestPageVersion ??
        (ShowNewRequestPageToggleButton && RequestPageVersionByDefault in RequestPageVersion
            ? RequestPageVersionByDefault
            : RequestPageVersion.V1_0);
    settings.RequestPageHideCoachmark = settings.RequestPageHideCoachmark ?? false;
};

export const useUserSettings = (): UserSettings => useReactiveVar(userSettingsVar);

// This update function must be called after the UserSettings are initalized.
// Actually Partial<UserSettings> is UserSettings, as each field in UserSettings is optional.
export const updateUserSettings = async (patch: Partial<UserSettings>): Promise<void> => {
    const logger = getAppHost().getLogger();

    // The memory cache are updated immediately no matter what the API call returns.
    userSettingsVar({ ...userSettingsVar(), ...patch });

    logger.logTraceInfo(LogCategory.UserSettings, `Try to update ${getUserSettingsRepr(patch)}`);
    const startTime = performance.now();
    try {
        const settings = await new UpdateUserSettingsClient().updateUserSettings(patch);
        logger.logTraceInfo(
            LogCategory.UserSettings,
            `Succeded to update settings and returned ${getUserSettingsRepr(settings)}, elapsed ${
                performance.now() - startTime
            }`
        );
    } catch (e) {
        logger.logTraceError(
            LogCategory.UserSettings,
            `Failed to initialize UserSettings with error ${errorToString(e)}, elapsed ${performance.now() - startTime}`
        );
    }
};
